import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import {
  Newspaper, MapPin, CalendarDays, ArrowLeft, ArrowRight, ExternalLink,
  ListChecks, CheckCircle2, MapPinned, HelpCircle,
} from "lucide-react";
import ShareButton from "@/components/ShareButton";
import FbGroupShare from "@/components/FbGroupShare";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import FaqJsonLd from "@/components/FaqJsonLd";
import FeaturedPartners from "@/components/FeaturedPartners";
import PressOffers from "@/components/PressOffers";
import PressComments from "@/components/PressComments";
import EventBrandMentions from "@/components/EventBrandMentions";
import LocalTopDeal from "@/components/LocalTopDeal";
import RelatedLocalEvents from "@/components/RelatedLocalEvents";
import PressReactions from "@/components/PressReactions";

// Mode curation : page PROXIreduc avec RÉSUMÉ court original (anti-copyright) + photo +
// encarts partenaires/bons plans + gros bouton vers l'article complet du média source.
// Le visiteur reste sur PROXIreduc (meilleures conversions) puis clique vers la source.
export default function PressArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/press-review/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: post ? `${post.title} | PROXIreduc-28` : "Actu locale | PROXIreduc-28",
    description: post ? (post.meta_description || post.excerpt || post.title) : "",
    canonicalPath: `/actu/${slug}`,
    image: post ? (post.cover_image || post.share_image || "/og-blog.jpg") : "/og-blog.jpg",
    keywords: post ? (post.keywords || []).join(", ") : undefined,
    type: "article",
  });

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="press-article-404">
      <h1 className="font-heading text-2xl font-bold">Actualité introuvable</h1>
      <Link to="/actu" className="mt-4 inline-flex items-center gap-1 text-[#E05638] font-semibold"><ArrowLeft size={16} /> Retour à l'actu locale</Link>
    </div>
  );
  if (!post) return <div className="max-w-2xl mx-auto px-4 py-20 text-slate-500">Chargement…</div>;

  const vitrineUrl = `${process.env.REACT_APP_BACKEND_URL}/api/actu-vitrine/${slug}`;
  const summary = post.excerpt || post.meta_description || "";
  const track = (ch) => { api.post(`/press-review/${slug}/share`, { channel: ch }).catch(() => {}); };
  const onSourceClick = () => track("source_click");

  const SourceCta = ({ testId }) => post.source_url ? (
    <a href={post.source_url} target="_blank" rel="noopener noreferrer" onClick={onSourceClick} data-testid={testId}
      className="inline-flex items-center gap-2 rounded-xl bg-[#E05638] hover:bg-[#c94a2f] text-white font-bold px-6 py-3.5 text-base transition-colors shadow-sm">
      Lire l'article complet sur {post.source_name || "le média"} <ExternalLink size={18} className="opacity-80" />
    </a>
  ) : null;

  return (
    <article className="pb-24 sm:pb-16" data-testid="press-article">
      {post.cover_image && (
        <div className="w-full aspect-[2.4/1] max-h-[460px] bg-slate-100 overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" data-testid="press-article-cover" />
        </div>
      )}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/actu" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E05638] bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3.5 py-2 rounded-full transition-colors" data-testid="press-article-back-top"><ArrowLeft size={15} /> Toute l'actu locale</Link>

        <div className="mt-4 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
          <span className="px-2 py-0.5 rounded-full bg-[#172A3A] text-white font-semibold">Actu locale</span>
          {post.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {post.city}</span>}
          {post.published_at && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(post.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>}
        </div>

        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="press-article-title">{post.title}</h1>

        {summary && <p className="mt-4 text-lg leading-relaxed text-slate-700" data-testid="press-article-summary">{summary}</p>}

        {/* L'essentiel en bref (puces factuelles originales) */}
        {post.key_points?.length > 0 && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="press-article-key-points">
            <div className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#172A3A]"><ListChecks size={16} className="text-[#E05638]" /> L'essentiel en bref</div>
            <ul className="mt-3 space-y-2">
              {post.key_points.map((k, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700" data-testid={`press-article-key-point-${i}`}>
                  <CheckCircle2 size={18} className="text-[#E05638] shrink-0 mt-0.5" /> <span>{k}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Angle local éditorial original */}
        {post.local_angle && (
          <div className="mt-6 rounded-2xl border-l-4 border-[#E05638] bg-orange-50/50 p-5" data-testid="press-article-local-angle">
            <div className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#E05638]"><MapPinned size={16} /> Pourquoi ça compte près de chez vous</div>
            <p className="mt-2 text-slate-700 leading-relaxed">{post.local_angle}</p>
          </div>
        )}

        {/* Gros CTA vers l'article complet du média source */}
        <div className="mt-6 rounded-2xl border border-orange-200 bg-orange-50/60 p-5" data-testid="press-article-source-box">
          <div className="text-sm text-slate-600">
            Résumé proposé par <span className="font-semibold text-slate-800">PROXIreduc-28</span>. L'intégralité de l'article est à lire sur le site du média.
          </div>
          <div className="mt-3"><SourceCta testId="press-article-source-cta" /></div>
          {post.source_name && <div className="mt-2 text-[11px] text-slate-400">Source : {post.source_name}</div>}
        </div>

        {/* Partage */}
        <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="press-article-share-box">
          <span className="text-sm font-bold text-slate-700 mr-1">Partager cette actu</span>
          <ShareButton url={vitrineUrl} title={post.title} text={post.title} testId="press-article-share" onShare={track} />
        </div>

        {/* Partage dans les groupes Facebook — juste sous le partage */}
        <div data-testid="press-article-fb-groups">
          <FbGroupShare small showTopCopy testId="press-article-fb-group-share" text={post.title} shareUrl={vitrineUrl} cityHint={post.city || ""} onShared={() => track("fb_groups")} />
        </div>

        {/* Réaction express (engagement) */}
        <PressReactions slug={slug} initial={post.reactions} testId="press-article-reactions" />

        {/* Bon plan du jour vedette — priorisé sur la ville de l'actu */}
        <LocalTopDeal city={post.city || ""} testId="press-article-top-deal" />

        {/* FAQ (valeur + rich snippets Google) — juste avant les partenaires */}
        {post.faq?.length > 0 && (
          <div className="mt-8 pt-6 border-t border-slate-100" data-testid="press-article-faq">
            <h2 className="font-heading text-xl font-bold text-[#172A3A] inline-flex items-center gap-2"><HelpCircle size={20} className="text-[#E05638]" /> Questions fréquentes</h2>
            <div className="mt-4 space-y-3">
              {post.faq.map((f, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`press-article-faq-${i}`}>
                  <div className="font-semibold text-slate-900">{f.q}</div>
                  <p className="mt-1 text-sm text-slate-600">{f.a}</p>
                </div>
              ))}
            </div>
            <FaqJsonLd faq={post.faq} />
          </div>
        )}

        {/* Encart partenaires discret — priorisé sur la ville de l'actu */}
        <div className="mt-8"><FeaturedPartners limit={5} testId="press-article-featured-partners" track city={post.city || ""} /></div>

        {/* Bons plans locaux */}
        <PressOffers limit={4} testId="press-article-press-offers" track city={post.city || ""} />

        {/* Rappel du CTA source, plus bas */}
        {post.source_url && (
          <div className="mt-8 text-center" data-testid="press-article-source-bottom">
            <SourceCta testId="press-article-source-cta-bottom" />
          </div>
        )}

        {/* Événements locaux liés */}
        <RelatedLocalEvents city={post.city || ""} testId="press-article-related-events" />

        {/* Commentaires */}
        <div className="mt-10 pt-6 border-t border-slate-100">
          <PressComments slug={slug} />
        </div>

        {/* Actus liées */}
        {post.related?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-100" data-testid="press-article-related">
            <h2 className="font-heading text-xl font-bold text-[#172A3A]">À lire aussi près de chez vous</h2>
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              {post.related.map((r) => (
                <Link key={r.id} to={`/actu/${r.slug}`} data-testid={`press-article-related-${r.slug}`}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-all">
                  <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden">
                    {r.cover_image
                      ? <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      : <div className="w-full h-full grid place-items-center text-[#E05638]/40"><Newspaper size={28} /></div>}
                  </div>
                  <div className="p-3">
                    {r.city && <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><MapPin size={11} /> {r.city}</div>}
                    <div className="mt-1 font-semibold text-sm text-slate-900 group-hover:text-[#E05638] transition-colors line-clamp-2">{r.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Actu locale", path: "/actu" }, { name: post.title }]} />

        {/* CTA inscription */}
        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-orange-50 to-white border border-orange-200 text-center">
          <div className="font-heading text-xl font-bold text-slate-900">Ne ratez plus rien de votre territoire</div>
          <p className="mt-1 text-sm text-slate-600">Rejoignez gratuitement le réseau des bons plans d'Eure-et-Loir.</p>
          <Link to="/inscription" className="mt-4 inline-flex items-center gap-1 btn-shine text-white font-semibold px-5 py-2.5 rounded-xl" data-testid="press-article-cta-signup">
            Créer mon compte gratuit <ArrowRight size={16} />
          </Link>
        </div>

        {/* Mentions de marque — tout en bas */}
        <div className="mt-10 pt-8 border-t border-slate-100" data-testid="press-article-brand-mentions-wrap">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Partenaires</div>
          <EventBrandMentions testId="press-article-brand-mentions" />
        </div>

        <div className="mt-10 text-center">
          <Link to="/actu" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E05638] hover:bg-[#c94a2f] px-5 py-2.5 rounded-xl transition-colors" data-testid="press-article-back-bottom">
            <ArrowLeft size={16} /> Toute l'actu locale
          </Link>
        </div>
      </div>
    </article>
  );
}
